import api from './api';

export interface RangeOSApiEndpoint {
  id: string;
  name: string;
  apiUrl: string;
  apiKey?: string;
  bearerToken?: string;
  description?: string;
  createdAt: string;
  lastUsed?: string;
  status: 'active' | 'inactive';
}

export interface RangeOSScenario {
  id: string;       // normalized from uuid or id
  uuid?: string;    // raw API field
  name: string;
  description?: string;
  version?: string;
  metadata?: Record<string, any>;
  packages?: string[];
  drafts?: string[];
  author?: string;
  dateCreated?: string;
  dateEdited?: string;
  eventTree?: string;
}

export interface RC5AU {
  id: string;
  title: string;
  description?: string;
  url?: string;
  rangeosScenarioName?: string;
  rangeosScenarioUUID?: string;
  mappedScenarioId?: string;
  mappedScenarioName?: string;
}

export interface RC5Config {
  course?: {
    id?: string;
    title?: string;
    description?: string;
  };
  au?: RC5AU[];
}

export interface CMI5ConfigResult {
  success: boolean;
  ausMapped: number;
  ausSkipped: number;
  ausFailed: number;
  details: Array<{
    auId: string;
    auTitle: string;
    status: 'mapped' | 'skipped' | 'failed';
    scenarioId?: string;
    scenarioName?: string;
    message?: string;
  }>;
}

// ============================================================================
// RangeOS API Endpoint Management
// ============================================================================

export const rangeosApiService = {
  /**
   * Get all RangeOS API endpoints
   */
  async getEndpoints(): Promise<RangeOSApiEndpoint[]> {
    const response = await api.get('/rangeos-api/endpoints');
    return response.data;
  },

  /**
   * Get a specific endpoint
   */
  async getEndpoint(id: string): Promise<RangeOSApiEndpoint> {
    const response = await api.get(`/rangeos-api/endpoints/${id}`);
    return response.data;
  },

  /**
   * Create a new endpoint
   */
  async createEndpoint(data: {
    name: string;
    apiUrl: string;
    apiKey?: string;
    bearerToken?: string;
    description?: string;
  }): Promise<RangeOSApiEndpoint> {
    const response = await api.post('/rangeos-api/endpoints', data);
    return response.data;
  },

  /**
   * Update an endpoint
   */
  async updateEndpoint(
    id: string,
    data: Partial<Omit<RangeOSApiEndpoint, 'id' | 'createdAt'>>
  ): Promise<RangeOSApiEndpoint> {
    const response = await api.put(`/rangeos-api/endpoints/${id}`, data);
    return response.data;
  },

  /**
   * Delete an endpoint
   */
  async deleteEndpoint(id: string): Promise<void> {
    await api.delete(`/rangeos-api/endpoints/${id}`);
  },

  /**
   * Test connection to an endpoint
   */
  async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/rangeos-api/endpoints/${id}/test`);
    return response.data;
  },

  /**
   * List scenarios from an endpoint
   */
  async listScenarios(endpointId: string): Promise<RangeOSScenario[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/scenarios`);
    return (response.data || []).map((s: any) => ({ ...s, id: s.id || s.uuid }));
  },

  /**
   * Search scenarios
   */
  async searchScenarios(endpointId: string, query: string): Promise<RangeOSScenario[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/scenarios/search`, {
      params: { q: query },
    });
    return (response.data || []).map((s: any) => ({ ...s, id: s.id || s.uuid }));
  },

  /**
   * List scenarios with full details (including packages field)
   * Normalizes uuid → id since the API may return either field
   */
  async listScenariosWithPackages(endpointId: string): Promise<RangeOSScenario[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/scenarios`);
    return (response.data || []).map((s: any) => ({
      ...s,
      id: s.id || s.uuid,
    }));
  },

  /**
   * Preview deleting a scenario (shows which packages would be orphaned)
   */
  async previewDeleteScenario(endpointId: string, uuid: string): Promise<{
    scenarioName: string;
    scenarioUuid: string;
    packages: Array<{ uuid: string; name: string; isOrphaned: boolean; otherScenarios: string[] }>;
  }> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/scenarios/${uuid}/delete-preview`);
    return response.data;
  },

  /**
   * Delete a scenario, optionally deleting orphaned packages
   */
  async deleteScenario(endpointId: string, uuid: string, deleteOrphanedPackages: boolean = false): Promise<{
    deletedScenario: string;
    deletedPackages: string[];
    skippedPackages: Array<{ name: string; reason: string }>;
    failedPackages: string[];
  }> {
    const response = await api.delete(`/rangeos-api/endpoints/${endpointId}/scenarios/${uuid}`, {
      params: { deleteOrphanedPackages },
    });
    return response.data;
  },

  /**
   * Export scenario bundle (local download)
   */
  async exportScenarioBundle(endpointId: string, uuid: string, includeReferencedPackages: boolean = false): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/scenarios/${uuid}/export-bundle`, {
      params: { includeReferencedPackages }
    });
    return response.data;
  },

  /**
   * Export scenario to S3
   */
  async exportScenarioToS3(
    endpointId: string,
    uuid: string,
    options: { targetEnvironment: string; filename?: string; includeReferencedPackages?: boolean }
  ): Promise<{ success: boolean; message: string; s3Key: string; name: string; packagesIncluded: number }> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/scenarios/${uuid}/export-to-s3`, options);
    return response.data;
  },

  /**
   * Batch export scenarios to S3
   */
  async batchExportScenariosToS3(
    endpointId: string,
    options: { targetEnvironment: string; uuids: string[]; includeReferencedPackages?: boolean }
  ): Promise<{ success: boolean; message: string; exported: string[]; failed: string[] }> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/scenarios/batch-export-to-s3`, options);
    return response.data;
  },

  /**
   * List scenario files from S3
   */
  async listScenarioFilesFromS3(
    environment: string
  ): Promise<Array<{ key: string; name: string; size: number; lastModified: string }>> {
    const response = await api.get('/s3/list-scenario-files', { params: { environment } });
    return response.data;
  },

  /**
   * Import scenario from S3
   */
  async importScenarioFromS3(
    endpointId: string,
    options: { s3Key: string; importReferencedPackages?: boolean; uuidMappings?: Array<{ oldUuid: string; newUuid: string }> }
  ): Promise<{ success: boolean; message: string; scenario: any; importedPackages: string[]; failedPackages: string[] }> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/scenarios/import-from-s3`, options);
    return response.data;
  },

  /**
   * Batch import scenarios from S3
   */
  async batchImportScenariosFromS3(
    endpointId: string,
    options: { s3Keys: string[]; importReferencedPackages?: boolean }
  ): Promise<{ success: boolean; message: string; imported: string[]; failed: string[] }> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/scenarios/batch-import-from-s3`, options);
    return response.data;
  },

  /**
   * Import scenario from JSON (local file)
   */
  async importScenarioFromJson(
    endpointId: string,
    data: any,
    importReferencedPackages: boolean = false,
    uuidMappings?: Array<{ oldUuid: string; newUuid: string }>
  ): Promise<{ success: boolean; message: string; scenario: any; importedPackages: string[]; failedPackages: string[] }> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/scenarios/import-from-json`, { data, importReferencedPackages, uuidMappings });
    return response.data;
  },

  /**
   * List design drafts
   */
  async listDesignDrafts(endpointId: string): Promise<any[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/design-drafts`);
    return response.data;
  },

  /**
   * Get a specific design draft
   */
  async getDesignDraft(endpointId: string, draftId: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/design-drafts/${draftId}`);
    return response.data;
  },

  /**
   * Get design state
   */
  async getDesignState(endpointId: string, draftId: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/design-drafts/${draftId}/states`);
    return response.data;
  },

  /**
   * Delete a draft
   */
  async deleteDraft(endpointId: string, draftId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/rangeos-api/endpoints/${endpointId}/design-drafts/${draftId}`);
    return response.data;
  },

  /**
   * Get draft scenario definition for export
   */
  async getDraftScenarioDefinition(endpointId: string, draftId: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/design-drafts/${draftId}/scenario-definition`);
    return response.data;
  },

  /**
   * Import draft from JSON
   */
  async importDraftFromJSON(
    endpointId: string,
    options: {
      name: string;
      description?: string;
      scenarioDefinition: any;
    }
  ): Promise<{ success: boolean; message: string; draftId: string }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/design-drafts/import-from-json`,
      options
    );
    return response.data;
  },

  /**
   * Export draft to S3
   */
  async exportDraftToS3(
    endpointId: string,
    draftId: string,
    options: {
      targetEnvironment: string;
      filename?: string;
    }
  ): Promise<{ success: boolean; message: string; s3Key: string; draftName: string }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/design-drafts/${draftId}/export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * Batch export multiple drafts to S3 (creates background job)
   */
  async batchExportDraftsToS3(
    endpointId: string,
    options: {
      targetEnvironment: string;
      draftIds: string[];
      filenamePrefix?: string;
    }
  ): Promise<{ success: boolean; message: string; jobId: string }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/design-drafts/batch-export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * Import draft from S3
   */
  async importDraftFromS3(
    endpointId: string,
    options: {
      sourceEnvironment: string;
      s3Key: string;
      draftName?: string;
    }
  ): Promise<{ success: boolean; message: string; draftId: string; draftName: string }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/design-drafts/import-from-s3`,
      options
    );
    return response.data;
  },

  /**
   * Transfer draft between RangeOS instances
   */
  async transferDraft(options: {
    sourceEndpointId: string;
    sourceDraftId: string;
    targetEndpointId: string;
    draftName?: string;
  }): Promise<{ success: boolean; message: string; draftId: string; draftName: string }> {
    const response = await api.post('/rangeos-api/transfer-draft', options);
    return response.data;
  },

  /**
   * List draft JSON files from S3
   */
  async listDraftFilesFromS3(
    environment: string
  ): Promise<Array<{ key: string; name: string; size: number; lastModified: string }>> {
    const response = await api.get('/s3/list-draft-files', {
      params: { environment }
    });
    return response.data;
  },

  // ==========================================================================
  // Ghost Traffic Management
  // ==========================================================================

  /**
   * List Ghost Traffic profiles from a RangeOS environment
   */
  async listGhostTraffic(endpointId: string): Promise<any[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/ghost-traffic`);
    return response.data;
  },

  /**
   * Get a specific Ghost Traffic profile
   */
  async getGhostTraffic(endpointId: string, uuid: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/ghost-traffic/${uuid}`);
    return response.data;
  },

  /**
   * Export Ghost Traffic to S3
   */
  async exportGhostTrafficToS3(
    endpointId: string,
    uuid: string,
    options: {
      targetEnvironment: string;
      filename?: string;
    }
  ): Promise<{ success: boolean; message: string; s3Key: string; name: string }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/ghost-traffic/${uuid}/export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * Batch export multiple Ghost Traffic profiles to S3
   */
  async batchExportGhostTrafficToS3(
    endpointId: string,
    options: {
      targetEnvironment: string;
      uuids: string[];
    }
  ): Promise<{ success: boolean; message: string; exported: string[]; failed: string[] }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/ghost-traffic/batch-export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * List Ghost Traffic JSON files from S3
   */
  async listGhostTrafficFilesFromS3(
    environment: string
  ): Promise<Array<{ key: string; name: string; size: number; lastModified: string }>> {
    const response = await api.get('/s3/list-ghost-traffic-files', {
      params: { environment }
    });
    return response.data;
  },

  /**
   * Import Ghost Traffic from S3 to RangeOS environment
   */
  async importGhostTrafficFromS3(
    endpointId: string,
    options: {
      s3Key: string;
      newName?: string;
    }
  ): Promise<{ success: boolean; message: string; ghostTraffic: any }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/ghost-traffic/import-from-s3`,
      options
    );
    return response.data;
  },

  /**
   * Batch import multiple Ghost Traffic profiles from S3
   */
  async batchImportGhostTrafficFromS3(
    endpointId: string,
    options: {
      s3Keys: string[];
    }
  ): Promise<{ success: boolean; message: string; imported: string[]; failed: string[] }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/ghost-traffic/batch-import-from-s3`,
      options
    );
    return response.data;
  },

  // ==========================================================================
  // Package Management
  // ==========================================================================

  /**
   * List Packages from a RangeOS environment
   */
  async listPackages(endpointId: string): Promise<any[]> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/packages`);
    return response.data;
  },

  /**
   * Get a specific Package
   */
  async getPackage(endpointId: string, uuid: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/packages/${uuid}`);
    return response.data;
  },

  /**
   * Export Package to S3 (with dependencies)
   */
  async exportPackageToS3(
    endpointId: string,
    uuid: string,
    options: {
      targetEnvironment: string;
      filename?: string;
      includeDependencies?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    s3Key: string;
    name: string;
    dependencies: {
      ghostTraffic: number;
      vmImages: number;
      charts: number;
      ansibleRoles: number;
      networks: number;
    };
  }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/packages/${uuid}/export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * Batch export multiple Packages to S3
   */
  async batchExportPackagesToS3(
    endpointId: string,
    options: {
      targetEnvironment: string;
      uuids: string[];
      includeDependencies?: boolean;
    }
  ): Promise<{ success: boolean; message: string; exported: string[]; failed: string[] }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/packages/batch-export-to-s3`,
      options
    );
    return response.data;
  },

  /**
   * List Package JSON files from S3
   */
  async listPackageFilesFromS3(
    environment: string
  ): Promise<Array<{ key: string; name: string; size: number; lastModified: string }>> {
    const response = await api.get('/s3/list-package-files', {
      params: { environment }
    });
    return response.data;
  },

  /**
   * Import Package from S3 to RangeOS environment
   */
  async importPackageFromS3(
    endpointId: string,
    options: {
      s3Key: string;
      newName?: string;
      importDependencies?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    package: any;
    importedDependencies: Record<string, string[]>;
    failedDependencies: Record<string, string[]>;
    uuidMapping?: Record<string, string>;
  }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/packages/import-from-s3`,
      options
    );
    return response.data;
  },

  /**
   * Export a Package bundle (with optional dependencies) for local download
   */
  async exportPackageBundle(endpointId: string, uuid: string, includeDependencies: boolean = true): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/packages/${uuid}/export-bundle`, {
      params: { includeDependencies }
    });
    return response.data;
  },

  /**
   * Import a Package from JSON (local file upload), supports bundle format with dependencies
   */
  async importPackageFromJson(endpointId: string, data: any, importDependencies: boolean = true): Promise<any> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/packages/import-from-json`, { data, importDependencies });
    return response.data;
  },

  /**
   * Preview what deleting a package with dependencies would do (dry run)
   */
  async previewDeletePackageWithDependencies(endpointId: string, uuid: string): Promise<{
    packageName: string;
    packageUuid: string;
    willDelete: Array<{ fieldName: string; assetType: string; uuid: string; name?: string }>;
    willSkipShared: Array<{ fieldName: string; assetType: string; uuid: string; name?: string; sharedWith: string[] }>;
    totalDependencies: number;
  }> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/packages/${uuid}/delete-preview`);
    return response.data;
  },

  /**
   * Delete a Package
   */
  async deletePackage(endpointId: string, uuid: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/rangeos-api/endpoints/${endpointId}/packages/${uuid}`);
    return response.data;
  },

  /**
   * Delete a Package with all its dependencies (shared deps are preserved)
   */
  async deletePackageWithDependencies(endpointId: string, uuid: string): Promise<{
    deletedPackage: boolean;
    deletedDependencies: Array<{ fieldName: string; assetType: string; uuid: string; name?: string }>;
    skippedShared: Array<{ fieldName: string; assetType: string; uuid: string; name?: string; sharedWith: string[] }>;
    failedDeletions: Array<{ fieldName: string; assetType: string; uuid: string; name?: string; error: string }>;
  }> {
    const response = await api.delete(`/rangeos-api/endpoints/${endpointId}/packages/${uuid}/with-dependencies`);
    return response.data;
  },

  /**
   * Export Ghost Traffic to local file (download JSON)
   */
  async exportGhostTrafficToLocal(endpointId: string, uuid: string): Promise<any> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/ghost-traffic/${uuid}`);
    return response.data;
  },

  /**
   * Import Ghost Traffic from JSON (local file upload)
   */
  async importGhostTrafficFromJson(endpointId: string, ghostTrafficData: any): Promise<any> {
    const response = await api.post(`/rangeos-api/endpoints/${endpointId}/ghost-traffic/import-from-json`, { ghostTrafficData });
    return response.data;
  },

  /**
   * Batch import multiple Packages from S3
   */
  async batchImportPackagesFromS3(
    endpointId: string,
    options: {
      s3Keys: string[];
      importDependencies?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    imported: string[];
    failed: string[];
    importedDependencies: Record<string, string[]>;
    failedDependencies: Record<string, string[]>;
  }> {
    const response = await api.post(
      `/rangeos-api/endpoints/${endpointId}/packages/batch-import-from-s3`,
      options
    );
    return response.data;
  },

  /**
   * Find orphaned objects in an environment
   */
  async findOrphanedObjects(endpointId: string, deep?: boolean): Promise<OrphanedObjectsReport> {
    const response = await api.get(`/rangeos-api/endpoints/${endpointId}/orphaned-objects`, {
      params: deep ? { deep: 'true' } : undefined,
    });
    return response.data;
  },

  /**
   * Delete a content asset by type and UUID
   */
  async deleteContentAsset(endpointId: string, assetType: string, uuid: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/rangeos-api/endpoints/${endpointId}/assets/${assetType}/${uuid}`);
    return response.data;
  },
};

// ============================================================================
// Orphaned Objects Types
// ============================================================================

export interface OrphanedObjectsReport {
  scenarios: {
    total: number;
    orphaned: Array<{ uuid: string; name: string; reason: string }>;
  };
  packages: {
    total: number;
    orphaned: Array<{ uuid: string; name: string; reason: string }>;
  };
  assets: {
    [assetType: string]: {
      total: number;
      orphaned: Array<{ uuid: string; name: string }>;
    };
  };
}

// ============================================================================
// CMI5 Activity Configuration
// ============================================================================

export const cmi5ConfigService = {
  /**
   * Get rc5.yaml content for a CMI5 activity
   */
  async getRC5Config(
    sourceId: string,
    tenantId: string,
    courseId: string
  ): Promise<RC5Config> {
    const response = await api.get(
      `/moodle/sources/${sourceId}/cmi5-activity/${tenantId}/${courseId}/rc5`
    );
    return response.data;
  },

  /**
   * Configure a CMI5 activity with RangeOS scenario mappings
   */
  async configureCMI5Activity(
    sourceId: string,
    tenantId: string,
    courseId: string,
    rangeosApiEndpointId: string,
    scenarioMappings: Array<{ auId: string; scenarioId: string; scenarioName: string }>
  ): Promise<CMI5ConfigResult> {
    const response = await api.post(`/moodle/sources/${sourceId}/configure-cmi5-activity`, {
      tenantId,
      courseId,
      rangeosApiEndpointId,
      scenarioMappings,
    });
    return response.data;
  },

  /**
   * Reconfigure an existing CMI5 activity
   */
  async reconfigureCMI5Activity(
    sourceId: string,
    moodleCourseId: number,
    moodleActivityId: number,
    rangeosApiEndpointId: string,
    scenarioMappings: Array<{ auId: string; scenarioId: string; scenarioName: string }>
  ): Promise<CMI5ConfigResult> {
    const response = await api.post(
      `/moodle/sources/${sourceId}/reconfigure-cmi5-activity`,
      {
        moodleCourseId,
        moodleActivityId,
        rangeosApiEndpointId,
        scenarioMappings,
      }
    );
    return response.data;
  },
};
